import React from 'react';
import {
    hasModifier,
    isIgnorableKeydown,
    type ShortcutScope,
    useIsShortcutScopeActive,
} from '@/components/shortcuts';

const FLASH_DURATION_MS = 150;
const DIGIT_PATTERN = /^[0-9]$/;

/**
 * `.` or `,` — whichever the seller's habit or layout writes — and the
 * numpad's decimal key by its physical position, since what it prints
 * varies (`,` under some layouts, `Delete` with Num Lock off).
 */
const isDecimalKey = (event: KeyboardEvent): boolean =>
    event.key === '.' || event.key === ',' || event.code === 'NumpadDecimal';

export type UsePaymentPhysicalKeysOptions = {
    /** `PAYMENT_SCOPE`, pushed by `PaymentScope` for as long as the payment
     * dialog is open. Keys are ignored unless it is the live top of the
     * shortcut stack, so a dialog opened on top (the cheat sheet) suspends
     * physical entry the same way it suspends every other shortcut. */
    scope: ShortcutScope;
    /** Same input operation the on-screen numpad's keys call. */
    onKey: (key: string) => void;
    onDelete: () => void;
    /** Mirrors the numpad's own backspace button: nothing to delete. */
    deleteDisabled: boolean;
    /** Confirms the payment; re-checks eligibility and its own in-flight lock, so a call that can't confirm (a short tender, a sale already posting) is safe. */
    confirm: () => void;
};

/**
 * Feeds the payment dialog's numpad from the physical keyboard: `0`-`9` and
 * the decimal point (`.`, `,` or the numpad's decimal key, all fed as `.`)
 * call `onKey`, `Backspace` calls `onDelete` — the same input path the
 * on-screen `Numpad` already uses, fed from anywhere in the dialog (bos-
 * seller#58's "the amount model stays pad-driven"). `Enter` confirms unless a
 * button has focus, in which case the native button activation already
 * covers it. Returns the key whose on-screen twin should flash, so the
 * physical and on-screen inputs read as one — the same shape as
 * `usePinPhysicalKeys`.
 *
 * Its own `keydown` listener rather than a `useShortcut` registration, for
 * the same reason `usePinPhysicalKeys` is: a keystroke here carries a digit
 * or a confirm, not a fixed command. It stays scope-aware even so.
 */
export const usePaymentPhysicalKeys = ({
    scope,
    onKey,
    onDelete,
    deleteDisabled,
    confirm,
}: UsePaymentPhysicalKeysOptions): string | null => {
    const isScopeActive = useIsShortcutScopeActive(scope);
    const [flashedKey, setFlashedKey] = React.useState<string | null>(null);
    const flashTimeoutRef = React.useRef<
        ReturnType<typeof setTimeout> | undefined
    >(undefined);

    const onKeyRef = React.useRef(onKey);
    onKeyRef.current = onKey;
    const onDeleteRef = React.useRef(onDelete);
    onDeleteRef.current = onDelete;
    const deleteDisabledRef = React.useRef(deleteDisabled);
    deleteDisabledRef.current = deleteDisabled;
    const confirmRef = React.useRef(confirm);
    confirmRef.current = confirm;

    const flash = React.useCallback((key: string) => {
        clearTimeout(flashTimeoutRef.current);
        setFlashedKey(key);
        flashTimeoutRef.current = setTimeout(
            () => setFlashedKey(null),
            FLASH_DURATION_MS,
        );
    }, []);

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (isIgnorableKeydown(event) || hasModifier(event)) {
                return;
            }
            if (!isScopeActive()) {
                return;
            }

            if (DIGIT_PATTERN.test(event.key)) {
                event.preventDefault();
                flash(event.key);
                onKeyRef.current(event.key);
                return;
            }
            if (isDecimalKey(event)) {
                event.preventDefault();
                flash('.');
                onKeyRef.current('.');
                return;
            }
            if (event.key === 'Backspace') {
                event.preventDefault();
                if (deleteDisabledRef.current) {
                    return;
                }
                flash('backspace');
                onDeleteRef.current();
                return;
            }
            if (event.key === 'Enter') {
                // A focused button activates itself on Enter natively — don't
                // double-fire confirm on top of it.
                if (event.target instanceof HTMLButtonElement) {
                    return;
                }
                event.preventDefault();
                confirmRef.current();
            }
        };
        // Capture phase: the method group is a real `RadioGroup`, and
        // react-aria's own Enter handling on a focused radio stops the
        // event's propagation before it would otherwise bubble to `window`
        // — the same reason `useCartKeyboardNav` reads keys ahead of the
        // design system's own handling.
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [isScopeActive, flash]);

    React.useEffect(() => () => clearTimeout(flashTimeoutRef.current), []);

    return flashedKey;
};
